import React, { useState, useMemo } from "react";
import styled from "styled-components";
import {
  Box,
  Button,
  Typography,
  TextField,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  InputAdornment,
} from "@mui/material";
import CircleCheck2 from "../Nucleo-Icons/icons/circle-check-2";
import { useDeleteModelData, usePutData } from "./tableHooks";
import ImportancyRange from "./ImportancyRange";
import SearchIcon from "@mui/icons-material/Search";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/Delete";
import DoneIcon from "@mui/icons-material/Done";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import {
  CloseButton,
  DeleteButton,
  EditButton,
  SelectButton,
} from "../../common/Buttons";
import { useParams } from "react-router-dom";

// 스타일 컴포넌트들
const Container = styled.div`
  width: 100%;
  height: 700px;
  margin: 0 auto;
  padding: 20px 20px 0px 20px;
  background-color: #ffffff;
  display: flex;
  flex-direction: column;
`;

const TitleArea = styled.div`
  margin-bottom: 1rem;
  margin-bottom: 1rem;
  display: flex;
  gap: 1rem;
  align-items: center;
  justify-content: space-between;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #111;
`;

const CardsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  flex: 1;
  overflow-y: scroll;
  overflow-x: hidden;
  &::-webkit-scrollbar {
    width: 10px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: rgba(99, 96, 150, 0.7);
    border-radius: 5px;
  }
  &::-webkit-scrollbar-track {
    background-color: transparent;
  }
`;

const Card = styled.div`
  padding: 15px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  background-color: #f9f9f9;
  position: relative;
  border: ${(props) => props.$isSelected && "2px solid #8b5cf6"};
`;

const CardTitle = styled.div`
  font-size: 16px;
  margin-bottom: 15px;
  color: #111;
  font-weight: bold;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardTitleDescription = styled.div`
  font-size: 14px;
  margin-bottom: 15px;
  color: #111;
  overflow: hidden;
  text-overflow: ellipsis;
`;

function RuleModal({
  modelParamList = [],
  onSelect,
  setModalOpen,
  selectedModel,
  openSnackbar,
}) {
  const { group_code, project_code } = useParams();
  const [editingModelId, setEditingModelId] = useState(null);
  const [editingModels, setEditingModels] = useState({});
  const [searchText, setSearchText] = useState("");
  const updateModelParameterMutation = usePutData(
    true,
    "/api/rule/editRule",
    openSnackbar
  );
  const deleteModelMutation = useDeleteModelData(
    "modelList",
    "/api/rule/deleteRule"
  );
  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
  };
  const filteredModels = useMemo(() => {
    if (!searchText.trim()) return modelParamList;
    const searchTerm = searchText.toLowerCase().trim();
    return modelParamList.filter((model) => {
      const nameMatch = model.model_name?.toLowerCase().includes(searchTerm);
      const descriptionMatch = model.model_description
        ?.toLowerCase()
        .includes(searchTerm);
      return nameMatch || descriptionMatch;
    });
  }, [modelParamList, searchText]);
  const handleEdit = (model) => {
    if (editingModelId === model.model_id) {
      const updatedModel = {
        ...model,
        ...editingModels[model.model_id],
        group_code,
        project_code,
      };
      updateModelParameterMutation.mutate(updatedModel, {
        onSuccess: () => {
          setEditingModelId(null);
        },
      });
    } else if (editingModelId && editingModelId !== model.model_id) {
      openSnackbar(`다른 규칙 수정을 진행중입니다. 먼저 완료해주세요.`, "info");
    } else {
      setEditingModels((prev) => ({
        ...prev,
        [model.model_id]: {
          model_name: model.model_name,
          model_description: model.model_description,
          learning_direction: model.learning_direction,
          leadtime_level: model.leadtime_level || 1,
          schedule_adherence: model.schedule_adherence || 1,
          duedate_compliance: model.duedate_compliance || 1,
          order_priority: model.order_priority || 1,
        },
      }));
      openSnackbar(`${model.model_name} 규칙 수정을 시작합니다.`);
      setEditingModelId(model.model_id);
    }
  };
  const handleValuesChange = (modelId, newValues) => {
    setEditingModels((prev) => ({
      ...prev,
      [modelId]: {
        ...prev[modelId],
        ...newValues,
      },
    }));
  };
  const handleInputChange = (modelId, field) => (event) => {
    setEditingModels((prev) => ({
      ...prev,
      [modelId]: {
        ...prev[modelId],
        [field]: event.target.value,
      },
    }));
  };
  const handleDirectionChange = (modelId) => (event) => {
    setEditingModels((prev) => ({
      ...prev,
      [modelId]: {
        ...prev[modelId],
        learning_direction: parseInt(event.target.value),
      },
    }));
  };
  const handleDeleteModel = (selectedModelIds) => {
    if (window.confirm("삭제하시겠습니까? 이 작업은 취소할 수 없습니다.")) {
      deleteModelMutation.mutate(selectedModelIds, {
        onSuccess: () => {
          openSnackbar("규칙을 성공적으로 삭제하였습니다.");
          setEditingModels({});
        },
      });
    }
  };
  return (
    <>
      <Container>
        <TitleArea>
          <Title>규칙 리스트</Title>
          <TextField
            placeholder="검색어를 입력하세요"
            variant="outlined"
            sx={{
              width: "35%",
              marginBottom: "0px",
            }}
            size="small"
            value={searchText}
            onChange={handleSearchChange}
            disabled={editingModelId !== null}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    color={editingModelId !== null ? "disabled" : "action"}
                  />
                </InputAdornment>
              ),
            }}
          />
        </TitleArea>
        <CardsContainer>
          {filteredModels.length > 0 ? (
            filteredModels.map((model) => {
              const isEditing = editingModelId === model.model_id;
              const editingModel = isEditing
                ? editingModels[model.model_id]
                : model;

              return (
                <Card
                  key={model.model_id}
                  $isSelected={selectedModel?.model_id === model.model_id}
                >
                  {isEditing ? (
                    // 편집 모드 헤더
                    <Box sx={{ padding: "0.5rem 1.5rem", mb: 2 }}>
                      <Box sx={{ mb: 2 }}>
                        <TextField
                          fullWidth
                          label="규칙 이름"
                          value={editingModel.model_name}
                          onChange={handleInputChange(
                            model.model_id,
                            "model_name"
                          )}
                          sx={{ mb: 2 }}
                        />
                        <TextField
                          fullWidth
                          label="규칙 설명"
                          value={editingModel.model_description}
                          onChange={handleInputChange(
                            model.model_id,
                            "model_description"
                          )}
                          multiline
                          rows={2}
                        />
                      </Box>
                      <FormControl component="fieldset">
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          학습 방향
                        </Typography>
                        <RadioGroup
                          row
                          value={editingModel.learning_direction}
                          onChange={handleDirectionChange(model.model_id)}
                        >
                          <FormControlLabel
                            value={0}
                            control={
                              <Radio
                                sx={{
                                  color: "#ff8787",
                                  "&.Mui-checked": { color: "#ff8787" },
                                }}
                              />
                            }
                            label="Backward"
                          />
                          <FormControlLabel
                            value={1}
                            control={
                              <Radio
                                sx={{
                                  color: "#1976d2",
                                  "&.Mui-checked": { color: "#1976d2" },
                                }}
                              />
                            }
                            label="Forward"
                          />
                        </RadioGroup>
                      </FormControl>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        gap: "1rem",
                        justifyContent: "space-between",
                        padding: "0.5rem 1.5rem",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          gap: "1rem",
                          position: "relative",
                        }}
                      >
                        {selectedModel?.model_id === model.model_id && (
                          <div
                            style={{
                              position: "absolute",
                              left: "-24px",
                              top: "4px",
                            }}
                          >
                            <CircleCheck2 />
                          </div>
                        )}
                        <CardTitle>{model.model_name}</CardTitle>
                        <CardTitleDescription>
                          {model.model_description}
                        </CardTitleDescription>
                      </div>
                      <Typography
                        variant="body2"
                        sx={{
                          ml: 2,
                          backgroundColor:
                            model.learning_direction === 0
                              ? "#f7eded"
                              : model.learning_direction === 1
                              ? "#edf6fc"
                              : "#7dfa87",
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          color:
                            model.learning_direction === 0
                              ? "#ff8787"
                              : model.learning_direction === 1
                              ? "#1976d2"
                              : "#127812",
                          height: "100%",
                        }}
                      >
                        {model.learning_direction === 1
                          ? "Forward"
                          : model.learning_direction === 2
                          ? "Back + Forward"
                          : "Backward"}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ padding: "0.5rem 1.5rem" }}>
                    <ImportancyRange
                      item={isEditing ? editingModel : model}
                      isEditing={isEditing}
                      onValuesChange={(values) =>
                        handleValuesChange(model.model_id, values)
                      }
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "1rem",
                      marginBottom: "0.5rem",
                    }}
                  >
                    <DeleteButton
                      onClick={() => handleDeleteModel(model.model_id)}
                    />
                    {/* <Button
                      variant="contained"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleDeleteModel(model.model_id)}
                    >
                      삭제
                    </Button> */}
                    <EditButton
                      color={isEditing ? "success" : "primary"}
                      onClick={() => handleEdit(model)}
                    >
                      {isEditing ? "수정완료" : "수정"}
                    </EditButton>
                    {/* <Button
                      variant="contained"
                      color={isEditing ? "success" : "primary"}
                      onClick={() => handleEdit(model)}
                      startIcon={<ModeEditIcon />}
                    >
                      {isEditing ? "수정완료" : "수정"}
                    </Button> */}
                    {isEditing && (
                      <DeleteButton
                        variant="contained"
                        color="error"
                        onClick={() => {
                          setEditingModelId(null);
                          openSnackbar("수정을 취소합니다.");
                        }}
                        startIcon={<CancelIcon />}
                      >
                        수정취소
                      </DeleteButton>
                    )}
                    <SelectButton
                      onClick={() => onSelect(model)}
                      disabled={isEditing}
                    />
                    {/* <Button
                      variant="contained"
                      color="success"
                      startIcon={<DoneIcon />}
                      onClick={() => onSelect(model)}
                      disabled={isEditing}
                    >
                      선택
                    </Button> */}
                  </Box>
                </Card>
              );
            })
          ) : (
            <Box sx={{ textAlign: "center", padding: "20px", color: "#666" }}>
              검색 결과가 없습니다.
            </Box>
          )}
        </CardsContainer>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            padding: "0.5rem 0",
          }}
        >
          <CloseButton
            onClick={() => {
              if (editingModelId) {
                openSnackbar(
                  "수정 중인 규칙이 있습니다. 먼저 완료해주세요.",
                  "warning"
                );
              } else {
                setModalOpen(false);
              }
            }}
          />
          {/* <Button
            variant="contained"
            color="error"
            startIcon={<CancelIcon />}
            onClick={() => {
              if (editingModelId) {
                openSnackbar(
                  "수정 중인 규칙이 있습니다. 먼저 완료해주세요.",
                  "warning"
                );
              } else {
                setModalOpen(false);
              }
            }}
          >
            닫기
          </Button> */}
        </Box>
      </Container>
    </>
  );
}

export default RuleModal;
